import { KeyRound, LogOut, Mailbox, Menu } from "lucide-react";
import { useState } from "react";

import logoMark from "@/assets/logo-mark.svg";
import { useAuth } from "@/auth/AuthContext";
import { useMailbox } from "@/auth/MailboxContext";
import { ChangePasswordDialog } from "@/components/account/ChangePasswordDialog";
import { Button } from "@/components/ui/button";

interface AppHeaderProps {
  onMenuOpen: () => void;
}

export function AppHeader({ onMenuOpen }: AppHeaderProps) {
  const { logout, isLoggingOut } = useAuth();
  const { mailboxes, activeMailbox, setActiveMailbox } = useMailbox();
  const [pwdDialogOpen, setPwdDialogOpen] = useState(false);

  async function handleLogout() {
    try {
      await logout();
    } catch {
      // no-op: AuthContext already resets the session state on failure via retry.
    }
  }

  return (
    <header className="glass-chrome sticky top-0 z-20 border-b border-white/50">
      <div className="flex h-16 items-center justify-between px-4 md:px-6">
        {/* Mobile left: hamburger + logo */}
        <div className="flex items-center gap-2 md:hidden">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0 text-slate-600 hover:bg-white/70"
            aria-label="Apri menu"
            onClick={onMenuOpen}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <img src={logoMark} alt="" aria-hidden="true" className="h-7 w-7" />
          <span className="font-display bg-gradient-to-br from-slate-900 to-blue-700 bg-clip-text text-lg font-semibold tracking-tight text-transparent">
            Nuntius
          </span>
        </div>

        {/* Desktop left: empty spacer (sidebar is separate) */}
        <div className="hidden md:block" />

        {/* Right side: mailbox selector + actions */}
        <div className="flex items-center gap-1">
          {mailboxes.length > 1 && (
            <div className="flex items-center gap-1.5 rounded-md border bg-white/60 px-2 py-1 text-sm">
              <Mailbox className="h-3.5 w-3.5 text-slate-500" />
              <select
                value={activeMailbox?.id ?? ""}
                onChange={(e) => setActiveMailbox(e.target.value)}
                className="cursor-pointer border-none bg-transparent text-sm text-slate-700 outline-none max-w-[120px] sm:max-w-none"
                aria-label="Mailbox attiva"
              >
                {mailboxes.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          {mailboxes.length === 1 && activeMailbox && (
            <span className="hidden items-center gap-1 text-xs text-muted-foreground sm:flex">
              <Mailbox className="h-3.5 w-3.5" />
              {activeMailbox.label}
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPwdDialogOpen(true)}
            className="text-slate-600 hover:bg-white/70 hover:text-slate-900"
            aria-label="Cambia password"
          >
            <KeyRound className="h-4 w-4" />
            <span className="hidden sm:inline">Cambia password</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="text-slate-600 hover:bg-white/70 hover:text-slate-900"
            aria-label="Logout"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">
              {isLoggingOut ? "Uscita..." : "Logout"}
            </span>
          </Button>
        </div>
      </div>
      <ChangePasswordDialog
        open={pwdDialogOpen}
        onOpenChange={setPwdDialogOpen}
      />
    </header>
  );
}
